/* eslint-disable camelcase */
import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useElements, ExpressCheckoutElement } from '@stripe/react-stripe-js'
import PaymentFields from '@/public/PaymentFields'
import { setError } from '@/public/redux/cartSlice'
import ModalButton from '@/public/ModalButton'
import { normalizePhone } from '@/shared/helpers/Errors'
import PolicyAgreements, { termsValues } from './PolicyAgreements'

export default function ExpressCheckout({
  submitOrder,
  cart,
  contents,
  terms_policy_modal_id,
  membershipAgreementUrl,
  hipaaPrivacyTermsUrl,
  customer,
}) {
  const [show, setShow] = useState(false)
  const [errors, setErrors] = useState({})
  const [dateOfBirth, setDateOfBirth] = useState(customer?.date_of_birth || '')
  const [sex, setSex] = useState(customer?.sex || '')
  const policyAgreementsCheckbox = useRef(null)
  const dispatch = useDispatch()

  const elements = useElements()

  useEffect(() => {
    if (customer?.date_of_birth) setDateOfBirth(customer.date_of_birth)
    if (customer?.sex) setSex(customer.sex)
  }, [customer?.date_of_birth, customer?.sex])

  const cartRequiresProfile = cart.contains_a_test
    || cart.contains_a_lab
    || cart.contains_a_membership

  const shouldShowProfileFields = cartRequiresProfile && (!dateOfBirth || !sex)

  function toCents(dollars) {
    return Math.round(dollars * 100)
  }

  function isUSCountry(country) {
    const normalized = typeof country === 'string' ? country.trim().toUpperCase() : ''
    return normalized === 'US'
  }

  function validateProfileFields() {
    const fieldErrors = {}

    if (!cartRequiresProfile) return fieldErrors

    if (!dateOfBirth) {
      fieldErrors.date_of_birth = { message: 'Your cart contains an age restricted item' }
    } else {
      const dob = new Date(dateOfBirth)
      const now = new Date()
      const eighteenYearsAgo = new Date(now.getFullYear() - 18, now.getMonth(), now.getDate())
      if (dob > eighteenYearsAgo) {
        fieldErrors.date_of_birth = { message: 'You must be at least 18 years old to order a lab or membership' }
      }
    }

    if (!sex) {
      fieldErrors.sex = { message: 'Sex is required to order a lab or membership' }
    }

    return fieldErrors
  }

  function validateAgreements() {
    if (!cart.contains_a_membership && !cart.contains_a_virtual_visit) {
      return {}
    }

    const agreementErrors = {}
    if (!policyAgreementsCheckbox.current?.checked) {
      agreementErrors.policy_agreements_checkbox = { message: 'Please agree to the terms to continue.' }
    }

    return agreementErrors
  }

  const lineItems = contents?.map((item) => ({
    name: (item.quantity > 1 ? `${item.data.name} x ${item.quantity}` : item.data.name),
    amount: toCents(Number(item.data.original_price)) * item.quantity,
  }))

  if (cart.calculated_amount_saved > 0) {
    lineItems.push({
      name: 'Savings',
      amount: -toCents(cart.calculated_amount_saved),
    })
  }

  if (cart.calculated_shipping_cost > 0) {
    lineItems.push({
      name: 'Shipping',
      amount: toCents(cart.calculated_shipping_cost),
    })
  }

  const onReady = ({ availablePaymentMethods }) => {
    if (availablePaymentMethods) { setShow(true) }
  }

  const onClick = ({ resolve }) => {
    const profileErrors = validateProfileFields()
    const validationErrors = validateAgreements()

    const allErrors = { ...profileErrors, ...validationErrors }
    setErrors(allErrors)

    if (Object.keys(allErrors).length > 0) {
      return
    }

    elements.update({ amount: toCents(cart.calculated_total_after_savings) })
    const options = {
      emailRequired: true,
      phoneNumberRequired: true,
      shippingAddressRequired: true,
      allowedShippingCountries: ['US'],
      lineItems,
      shippingRates: [
        {
          id: 'shipping',
          displayName: 'Standard Shipping',
          amount: toCents(cart.calculated_shipping_cost),
          deliveryEstimate: {
            maximum: { unit: 'day', value: 7 },
            minimum: { unit: 'day', value: 5 },
          },
        },
      ],
    }

    resolve(options)
  }

  const onShippingAddressChange = async ({ resolve, reject, address }) => {
    if (!isUSCountry(address.country)) {
      reject('We currently only ship within the United States')
      return
    }
    resolve()
  }

  const onConfirm = async (event) => {
    const billingDetails = event.billingDetails
    const billingAddress = billingDetails.address
    const shippingAddress = event.shippingAddress.address

    if (!isUSCountry(shippingAddress.country)) {
      dispatch(setError('We currently only ship within the United States. Please update your shipping address and try again.'))
      event.paymentFailed({ reason: 'invalid_shipping_address' })
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    dispatch(setError(null))

    const names = billingDetails.name.split(' ')
    const first_name = names.shift()
    const last_name = names.join(' ')
    const email = billingDetails.email
    const phone = normalizePhone(billingDetails.phone)

    const customerData = {
      first_name,
      last_name,
      phone,
      email,
      date_of_birth: dateOfBirth || undefined,
      sex: sex || undefined,
      billing: {
        address_line_1: billingAddress.line1,
        address_line_2: billingAddress.line2,
        city: billingAddress.city,
        state: billingAddress.state,
        zip: billingAddress.postal_code,
        country: billingAddress.country,
      },
      shipping: {
        address_line_1: shippingAddress.line1,
        address_line_2: shippingAddress.line2,
        city: shippingAddress.city,
        state: shippingAddress.state,
        zip: shippingAddress.postal_code,
        country: shippingAddress.country,
      },
    }

    submitOrder(customerData, termsValues(cart))
  }

  return (
    <>
      {show && <p className="mb-2 text-center">Express Checkout</p>}
      {show && shouldShowProfileFields && (
        <div className="row mb-3">
          <div className="col-6">
            <label htmlFor="express-date-of-birth" className="form-label">Date of Birth</label>
            <input
              id="express-date-of-birth"
              type="date"
              className={`form-control ${errors.date_of_birth ? 'error' : ''}`}
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
            />
            {errors.date_of_birth && (
              <span className="text-danger">{errors.date_of_birth.message}</span>
            )}
          </div>
          <div className="col-6">
            <label htmlFor="express-sex" className="form-label">Sex</label>
            {/* `react` keeps scripts/custom.js's customSelect off this controlled
                select: its jQuery-only change event never reaches onChange. */}
            <select
              id="express-sex"
              className={`form-control react ${errors.sex ? 'error' : ''}`}
              value={sex}
              onChange={(e) => setSex(e.target.value)}
            >
              <option value="">Select</option>
              <option value="F">Female</option>
              <option value="M">Male</option>
              <option value="?">Prefer not to say</option>
            </select>
            {errors.sex && (
              <span className="text-danger">{errors.sex.message}</span>
            )}
          </div>
        </div>
      )}
      {show && !shouldShowProfileFields && errors.date_of_birth && (
        <div className="mb-3">
          <span className="text-danger">{errors.date_of_birth.message}</span>
        </div>
      )}
      {/* Wallet buttons are an extra: when Stripe cannot draw them they go,
          and the regular payment field below is still the way to pay. */}
      <PaymentFields source="checkout.express" fallback={null}>
        {(onLoadError) => (
          <ExpressCheckoutElement
            onConfirm={onConfirm}
            onClick={onClick}
            onReady={onReady}
            onShippingAddressChange={onShippingAddressChange}
            onLoadError={onLoadError}
          />
        )}
      </PaymentFields>
      {show && (cart.contains_a_membership || cart.contains_a_virtual_visit) && (
        <PolicyAgreements
          cart={cart}
          errors={errors}
          membershipAgreementUrl={membershipAgreementUrl}
          hipaaPrivacyTermsUrl={hipaaPrivacyTermsUrl}
          checkboxRef={policyAgreementsCheckbox}
        />
      )}
      {show && (
        <>
          <div className="mt-2">
            <span>
              By clicking "Pay" you agree to our
            </span>
            <ModalButton
              id={terms_policy_modal_id}
              className="btn btn-link py-0 ps-1 mb-1"
              title="Click to view our terms and conditions"
              text="Terms & Conditions*"
            />
          </div>
          <div className="d-flex align-items-center justify-content-between">
            <hr className="split-divider" />
            <span>OR</span>
            <hr className="split-divider" />
          </div>
        </>
      )}
    </>
  )
}

ExpressCheckout.propTypes = {
  submitOrder: PropTypes.func.isRequired,
  cart: PropTypes.shape({
    calculated_shipping_cost: PropTypes.string.isRequired,
    calculated_amount_saved: PropTypes.string.isRequired,
    calculated_total_after_savings: PropTypes.string.isRequired,
  }).isRequired,
  contents: PropTypes.arrayOf(PropTypes.shape({
    quantity: PropTypes.number.isRequired,
    data: PropTypes.shape({
      name: PropTypes.string.isRequired,
      original_price: PropTypes.number.isRequired,
    }),
  })),
  terms_policy_modal_id: PropTypes.string.isRequired,
  customer: PropTypes.shape({
    date_of_birth: PropTypes.string,
    sex: PropTypes.string,
  }),
}

ExpressCheckout.defaultProps = {
  contents: null,
  customer: null,
}
